import { FunctionComponent, useContext } from 'react'
import styled, { css } from 'styled-components'
import { ellipsis } from 'polished'
import contextSportsHeader, {
  SportsHeaderContextType,
} from '@contexts/sportsHeader'
import { TennisScoreTableProps } from '@widgets/Teaser/sportsHeader/types'
import { computeTeaserWidth } from '@widgets/Teaser/utils'
import { ViewportTypeValues } from '@contexts/viewport'
import { desktopCSS, tabletCSS, mobileCSS } from '@measures/responsive'

type StyledPlayerNameProps = Pick<SportsHeaderContextType, 'teaserFlex'>
type StyledGameScoreProps = Pick<SportsHeaderContextType, 'teaserFlex'>

interface StyledTennisScoreTableProps {
  numberOfGamesCompleted: number
  hasGameInProgress: boolean
}

interface StyledSetScoreProps
  extends Pick<SportsHeaderContextType, 'teaserFlex'> {
  isPending: boolean
}

const { SportsHeaderContext } = contextSportsHeader

const StyledTennisScoreTable = styled.div<StyledTennisScoreTableProps>`
  ${({ numberOfGamesCompleted, hasGameInProgress }) => {
    const gridColumnNumber =
      1 + numberOfGamesCompleted + (hasGameInProgress ? 1 : 0)
    return css`
      display: grid;
      width: 100%;
      box-sizing: border-box;
      padding: 6px 8px;
      grid-template-columns: minmax(0, 1fr) repeat(
          ${gridColumnNumber - 1},
          auto
        );
      grid-column-gap: 10px;
      grid-row-gap: 6px;
    `
  }}
`

const StyledPlayerName = styled.div<StyledPlayerNameProps>`
  justify-self: flex-start;
  ${ellipsis('100%', 1)};
  ${({
    theme: {
      colors: { white },
      fonts: { goodOT: goodOTFontName },
    },
    teaserFlex,
  }) => {
    let fontSizeDesktop
    const desktopTeaserFlex = teaserFlex['desktop']
    const teaserWidthDesktop = computeTeaserWidth({
      flexValue: desktopTeaserFlex,
      viewportType: ViewportTypeValues.DESKTOP,
    })

    if (desktopTeaserFlex === '6') {
      fontSizeDesktop = `${0.01686 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '4') {
      fontSizeDesktop = `${0.0256 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '3') {
      fontSizeDesktop = `${0.02845 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '2') {
      fontSizeDesktop = `${0.04375 * teaserWidthDesktop}px`
    }

    let fontSizeTablet
    const tabletTeaserFlex = teaserFlex['tablet']
    const teaserWidthTablet = computeTeaserWidth({
      flexValue: tabletTeaserFlex,
      viewportType: ViewportTypeValues.TABLET,
    })

    if (tabletTeaserFlex === '6') {
      fontSizeTablet = `${0.02845 * teaserWidthTablet}px`
    } else if (tabletTeaserFlex === '3') {
      fontSizeTablet = `${0.04375 * teaserWidthTablet}px`
    }

    return css`
      color: ${white};
      font-family: ${goodOTFontName}, 'Arial Narrow', Impact;
      line-height: 1;
      font-weight: 900;
      ${desktopCSS(css`
        font-size: ${fontSizeDesktop};
      `)}

      ${tabletCSS(css`
        font-size: ${fontSizeTablet};
      `)}

        ${mobileCSS(css`
        font-size: 3.734vw;
      `)}
    `
  }}
`

const StyledSetScore = styled.div<StyledSetScoreProps>`
  justify-self: flex-end;
  ${({
    theme: {
      colors: {
        white,
        grey: { grey_50 },
      },
      fonts: { goodOT: goodOTFontName },
    },
    isPending,
    teaserFlex,
  }) => {
    let fontSizeDesktop
    const desktopTeaserFlex = teaserFlex['desktop']
    const teaserWidthDesktop = computeTeaserWidth({
      flexValue: desktopTeaserFlex,
      viewportType: ViewportTypeValues.DESKTOP,
    })

    if (desktopTeaserFlex === '6') {
      fontSizeDesktop = `${0.01686 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '4') {
      fontSizeDesktop = `${0.0256 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '3') {
      fontSizeDesktop = `${0.02845 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '2') {
      fontSizeDesktop = `${0.04375 * teaserWidthDesktop}px`
    }

    let fontSizeTablet
    const tabletTeaserFlex = teaserFlex['tablet']
    const teaserWidthTablet = computeTeaserWidth({
      flexValue: tabletTeaserFlex,
      viewportType: ViewportTypeValues.TABLET,
    })

    if (tabletTeaserFlex === '6') {
      fontSizeTablet = `${0.02845 * teaserWidthTablet}px`
    } else if (tabletTeaserFlex === '3') {
      fontSizeTablet = `${0.04375 * teaserWidthTablet}px`
    }

    return css`
      color: ${isPending ? grey_50 : white};
      font-family: ${goodOTFontName}, 'Arial Narrow', Impact;
      line-height: 1;
      font-weight: 900;
      ${desktopCSS(css`
        font-size: ${fontSizeDesktop};
      `)}

      ${tabletCSS(css`
        font-size: ${fontSizeTablet};
      `)}

      ${mobileCSS(css`
        font-size: 3.734vw;
      `)}
    `
  }}
`

const StyledGameScore = styled.div<StyledGameScoreProps>`
  justify-self: flex-end;
  ${({
    theme: {
      colors: {
        brandColors: { brand_55 },
      },
      fonts: { goodOT: goodOTFontName },
    },
    teaserFlex,
  }) => {
    let fontSizeDesktop
    const desktopTeaserFlex = teaserFlex['desktop']
    const teaserWidthDesktop = computeTeaserWidth({
      flexValue: desktopTeaserFlex,
      viewportType: ViewportTypeValues.DESKTOP,
    })

    if (desktopTeaserFlex === '6') {
      fontSizeDesktop = `${0.01686 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '4') {
      fontSizeDesktop = `${0.0256 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '3') {
      fontSizeDesktop = `${0.02845 * teaserWidthDesktop}px`
    } else if (desktopTeaserFlex === '2') {
      fontSizeDesktop = `${0.04375 * teaserWidthDesktop}px`
    }

    let fontSizeTablet
    const tabletTeaserFlex = teaserFlex['tablet']
    const teaserWidthTablet = computeTeaserWidth({
      flexValue: tabletTeaserFlex,
      viewportType: ViewportTypeValues.TABLET,
    })

    if (tabletTeaserFlex === '6') {
      fontSizeTablet = `${0.0256 * teaserWidthTablet}px`
    } else if (tabletTeaserFlex === '3') {
      fontSizeTablet = `${0.02845 * teaserWidthTablet}px`
    }

    return css`
      color: ${brand_55};
      font-family: ${goodOTFontName}, 'Arial Narrow', Impact;
      line-height: 1;
      font-weight: 900;
      ${desktopCSS(css`
        font-size: ${fontSizeDesktop};
      `)}

      ${tabletCSS(css`
        font-size: ${fontSizeTablet};
      `)}

      ${mobileCSS(css`
        font-size: 3.734vw;
      `)}
    `
  }}
`

const TennisScoreTable: FunctionComponent<TennisScoreTableProps> = ({
  homePlayer: { name: homePlayerName },
  awayPlayer: { name: awayPlayerName },
  tennisGamesCompleted,
  tennisGameInProgress,
}) => {
  const { teaserFlex } = useContext(SportsHeaderContext)

  return (
    <StyledTennisScoreTable
      numberOfGamesCompleted={tennisGamesCompleted.length}
      hasGameInProgress={!!tennisGameInProgress}>
      <StyledPlayerName teaserFlex={teaserFlex}>
        {homePlayerName}
      </StyledPlayerName>
      {tennisGamesCompleted.map((tennisSet, index, tennisArray) => (
        <StyledSetScore
          teaserFlex={teaserFlex}
          key={tennisSet.number}
          isPending={
            !!(index === tennisArray.length - 1 && tennisGameInProgress)
          }>
          {tennisSet.home_score}
        </StyledSetScore>
      ))}
      {tennisGameInProgress ? (
        <StyledGameScore teaserFlex={teaserFlex}>
          {tennisGameInProgress.sdpHomeScore}
        </StyledGameScore>
      ) : null}
      <StyledPlayerName teaserFlex={teaserFlex}>
        {awayPlayerName}
      </StyledPlayerName>
      {tennisGamesCompleted.map((tennisSet, index, tennisArray) => (
        <StyledSetScore
          teaserFlex={teaserFlex}
          key={tennisSet.number}
          isPending={
            !!(index === tennisArray.length - 1 && tennisGameInProgress)
          }>
          {tennisSet.away_score}
        </StyledSetScore>
      ))}
      {tennisGameInProgress ? (
        <StyledGameScore teaserFlex={teaserFlex}>
          {tennisGameInProgress.sdpAwayScore}
        </StyledGameScore>
      ) : null}
    </StyledTennisScoreTable>
  )
}

export default TennisScoreTable
